/**
 * ToolCard — compact tool card with action buttons.
 *
 * Shows tool name, category badge, and action buttons (visible on hover).
 * Description shown as a simple non-interactive tooltip.
 *
 * Phase 5 — Unit 5.4
 */

import { Plus,Star } from 'lucide-react';
import React from 'react';

import { ToolTypeIcon } from '@/components/common/ToolTypeIcon';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ITool } from '@/interfaces/ITool';
import { cn } from '@/lib/utils';
import { useToolBrowserStore } from '@/store/useToolBrowserStore';

export interface ToolCardProps {
  tool: ITool;
  isFavorite?: boolean;
  onToggleFavorite?: (toolId: string) => void;
  onSelect: (toolId: string) => void;
  onAddToPipeline: (toolId: string) => void;
  /** Display as grid card instead of compact row */
  grid?: boolean;
}



export const ToolCard: React.FC<ToolCardProps> = ({
  tool,
  isFavorite: isFavoriteProp,
  onToggleFavorite: onToggleFavoriteProp,
  onSelect,
  onAddToPipeline,
  grid = false,
}) => {
  // Subscribe directly to store for reliable favorite state
  const storeFavorites = useToolBrowserStore((s) => s.favorites);
  const storeToggle = useToolBrowserStore((s) => s.toggleFavorite);
  const isFavorite = isFavoriteProp ?? storeFavorites.includes(tool.id);
  const onToggleFavorite = onToggleFavoriteProp ?? storeToggle;

  if (grid) {
    return (
      <div
        className="group relative flex flex-col gap-1.5 rounded-lg border border-border p-3
                   hover:bg-accent/60 hover:border-accent transition-colors cursor-pointer"
        onClick={() => onSelect(tool.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onSelect(tool.id)}
      >
        <div className="flex items-start justify-between gap-1">
          <span className="text-sm font-medium leading-tight line-clamp-2">{tool.name}</span>
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              className="p-0.5 rounded hover:bg-accent opacity-60 transition-opacity"
              onClick={(e) => { e.stopPropagation(); onAddToPipeline(tool.id); }}
              aria-label="Add to pipeline"
              title="Add to pipeline"
            >
              <Plus className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>
            <button
              className="p-0.5 rounded hover:bg-accent"
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(tool.id); }}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star className={cn('h-3.5 w-3.5', isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground')} />
            </button>
          </div>
        </div>
        <ToolTypeIcon type={tool.type} size="sm" className="self-start" />
        <span className="text-xs text-muted-foreground line-clamp-2">{tool.description}</span>
      </div>
    );
  }

  // Compact row layout (default)
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="group flex items-center gap-2 rounded-md px-2 py-1.5
                       hover:bg-accent/60 transition-colors cursor-pointer"
            onClick={() => onSelect(tool.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onSelect(tool.id)}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{tool.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {tool.category ?? 'Uncategorized'}
              </div>
            </div>

            <ToolTypeIcon type={tool.type} size="sm" />

            {/* Action buttons — visible on hover */}
            <button
              className="shrink-0 p-1 rounded hover:bg-accent opacity-60 transition-opacity"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAddToPipeline(tool.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label="Add to pipeline"
              title="Add to pipeline"
            >
              <Plus className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
            </button>

            <button
              className="shrink-0 p-1 rounded hover:bg-accent"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleFavorite(tool.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Star className={cn('h-3.5 w-3.5', isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground')} />
            </button>
          </div>
        </TooltipTrigger>
        {/* Non-interactive description tooltip */}
        <TooltipContent side="right" sideOffset={8} className="max-w-[240px]">
          <p className="text-xs leading-relaxed">{tool.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};


